import { useState, useEffect, useRef } from "react";

// ============================================================
// BINARY GAME: NEXT STEPS QUALIFIER
// Client: Nathaniel Reagan (AILL)
// Route: /sales/nathaniel
// ============================================================

const TREE_DEFINITION = {
  title: "Next Steps Qualifier",
  subtitle: "Binary Game Decision Tree // Nathaniel // 2026",
  intro: {
    heading: "Before we start.",
    body: "This is a decision tree. It works the same way as the ones I build for clients. You answer a few honest questions. The right option shows up at the end. No pitch. No pressure. Just clarity.",
    cta: "I'm ready",
  },
  gates: {
    gate1: {
      id: "gate1",
      type: "this-that",
      label: "GATE 01",
      question: "What is the most important thing to solve right now?",
      subtitle:
        "If you could only work on one thing in the next 30 days, what would it be?",
      optionA: {
        label: "THE PERSONAL STUFF",
        description:
          "Visibility. Control. How I show up. Fear of judgment. My relationship with being the public face of this thing.",
      },
      optionB: {
        label: "THE BUSINESS STUFF",
        description:
          "Systems. Rules. Team structure. Content flow. Getting the company to run without everything going through me.",
      },
    },
    gate2: {
      id: "gate2",
      type: "this-that",
      label: "GATE 02",
      question: "How much are you willing to invest in the personal work?",
      optionA: { label: "MORE THAN $5,000" },
      optionB: { label: "LESS THAN $5,000" },
    },
    gate3: {
      id: "gate3",
      type: "this-that",
      label: "GATE 03",
      question: "How much are you willing to invest in the business work?",
      optionA: { label: "MORE THAN $5,000" },
      optionB: { label: "LESS THAN $5,000" },
    },
  },

  offers: {
    personal_high: {
      id: "personal_high",
      badge: "PERSONAL / MORE THAN $5K",
      title: "The Main Character Experience",
      price: "$5,000 to $10,000",
      priceSub: "depending on scope",
      body: "One to three days. In person. A location we choose together. Me and Anaya. We go all the way in on identity, visibility, control, and leadership. This is not incremental. It is a reset. We have stellar results with this container.",
      detail:
        "Anaya is an intuition trainer and somatic healer. She is my partner and my personal secret for a lot of my own internal wisdom and support. She is world class at helping people strengthen their ability to trust themselves, stop gauging every decision against some fearful future, and work through whatever is sitting in the body around rejection and exposure.",
      detail2: "Together, we build the container around you. You come out operating differently.",
      bullets: [
        "1 to 3 days based on scope and investment",
        "Marco + Anaya as co-facilitators",
        "Pre-session preparation",
        "Follow-up session included",
        "Written deliverables from the experience",
      ],
      cta: "Let's talk about scheduling this.",
      ctaLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
      chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
      learnMoreLink: "http://www.notmarcohansell.com/main-character",
    },
    personal_low: {
      id: "personal_low",
      badge: "PERSONAL / LESS THAN $5K",
      title: "Two paths here.",
      anayaIntro: "Anaya is an intuition trainer and somatic healer. She is Marco's partner and his personal secret for a lot of his own internal wisdom and support. She is world class at helping people strengthen their ability to trust themselves and work through whatever is sitting in the body around rejection and exposure.",
      options: [
        {
          title: "The Trust Experience with Anaya",
          price: "$3,000/month",
          body: "Anaya meets with you weekly. Two hours each session. She works with you directly on the visibility, nervous system, control, and identity patterns. Strengthening your ability to trust yourself. Working through the somatics around rejection and exposure. Not just talking about it. Moving it in your body.",
          detail:
            "Marco stays in the loop with oversight. This is managed through him, not separate. You get her depth and his strategic eye making sure it connects to the business.",
          bullets: [
            "Weekly 2-hour sessions with Anaya",
            "Marco oversight and strategic input",
            "Managed through Marco",
          ],
          cta: "Let's set this up.",
          ctaLink: "https://buy.stripe.com/14AdRa6X98uv182c2GcMM0w",
          chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
        },
        {
          title: "Binary Access with Marco",
          price: "$3,000/month",
          body: "Me in your pocket. Voice notes. You can hit me with personal stuff, business stuff, whatever is live for you. I respond within 24 hours. I help calibrate you to the binary decisions you are facing in real time. No scheduled calls. Just access. This is not coaching. I am going to surface awareness and ask binary questions. The actual work is yours.",
          bullets: [
            "Voice note access (daily if needed)",
            "24-hour response time",
            "Personal and business questions both welcome",
            "No scheduled meetings",
          ],
          cta: "Let's start this.",
          ctaLink: "https://buy.stripe.com/6oUfZi1CPbGHdUOeaOcMM0v",
          chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
        },
      ],
    },
    business_high: {
      id: "business_high",
      badge: "BUSINESS / MORE THAN $5K",
      title: "Binary Command",
      price: "$5,000+",
      priceSub: "based on number of systems",
      body: "Full implementation. I scope out which decision tracks we are building and I build them. Each system is $1,000 and takes one meeting, max two. At $5,000+ you get five or more systems, plus the parent system: the overlapping logic that connects all your trees into one operating system.",
      bullets: [
        "5+ Binary Build systems ($1,000 each)",
        "Max 2 meetings per system",
        "Parent system (interlocking logic between trees)",
        "Team-facing deliverables stored in dashboard",
      ],
      cta: "Let's talk about scoping this.",
      ctaLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
      chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
    },
    business_low: {
      id: "business_low",
      badge: "BUSINESS / LESS THAN $5K",
      title: "Three options based on depth.",
      options: [
        {
          title: "Binary Build",
          price: "$1,000 per system",
          body: "Pick a track. I build it out to completion. One 90-minute meeting. You get a finished decision tree with clear rules, the exceptions, and any fill-in-the-blanks for things the team still needs to decide. No ongoing commitment. Delivered within three business days.",
          bullets: [
            "One 90-minute session",
            "Finished tree delivered within 3 business days",
            "No ongoing commitment",
          ],
          cta: "Pick a track. Let's build it.",
          ctaLink: "https://book.stripe.com/28EaEYdlxh112c66ImcMM0p",
          chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
        },
        {
          title: "Binary Access with Marco",
          price: "$3,000/month",
          body: "Me in your pocket. Voice notes. You hit me with operational questions, hiring decisions, content calls, prioritization, whatever is live. I respond within 24 hours with binary framing. I am not building full systems in this container. I am helping you make sharper decisions in real time.",
          bullets: [
            "Voice note access (daily if needed)",
            "24-hour response time",
            "Business-focused binary calibration",
            "No scheduled meetings",
          ],
          cta: "Let's start this.",
          ctaLink: "https://buy.stripe.com/6oUfZi1CPbGHdUOeaOcMM0v",
          chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
        },
        {
          title: "Binary Solve",
          price: "$300 each",
          body: "Simple decisions. Not complex trees with multiple layers. You bring me a single decision. I help you sort it in one call. You leave with a clear yes or no and the reasoning behind it. Fast. Light. Good for when you just need one thing unstuck.",
          bullets: [
            "One decision per session",
            "30-minute call",
            "Clear output same day",
          ],
          cta: "Book a solve.",
          ctaLink: "https://book.stripe.com/14A5kE81ddOP7wq9UycMM0o",
          chatLink: "https://app.usemotion.com/meet/marco-hansell/VIP?d=30",
        },
      ],
    },
  },

  resolveSequence: (answers) => {
    const seq = ["gate1"];
    if (!answers["gate1"]) return seq;

    if (answers["gate1"] === "THE PERSONAL STUFF") {
      seq.push("gate2");
      if (!answers["gate2"]) return seq;
      if (answers["gate2"] === "MORE THAN $5,000") {
        seq.push("offer:personal_high");
      } else {
        seq.push("offer:personal_low");
      }
    } else {
      seq.push("gate3");
      if (!answers["gate3"]) return seq;
      if (answers["gate3"] === "MORE THAN $5,000") {
        seq.push("offer:business_high");
      } else {
        seq.push("offer:business_low");
      }
    }
    return seq;
  },
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#F5F3EF",
    fontFamily: "'Work Sans', -apple-system, sans-serif",
    color: "#1a1a1a",
  },
  container: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "40px 20px 80px",
  },
  header: {
    marginBottom: 48,
  },
  headerMeta: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.1em",
    color: "#999",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  headerTitle: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 36,
    fontWeight: 700,
    color: "#1a1a1a",
    lineHeight: 1.2,
    marginBottom: 8,
  },
  headerSubtitle: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12,
    color: "#999",
    letterSpacing: "0.04em",
  },
  divider: {
    width: "100%",
    height: 1,
    background: "#e0ddd8",
    margin: "32px 0",
  },
  // Intro card
  introCard: {
    background: "#fff",
    borderLeft: "4px solid #2563EB",
    borderRadius: 2,
    padding: "28px 32px",
    marginBottom: 0,
  },
  introHeading: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16,
    color: "#1a1a1a",
  },
  introBody: {
    fontSize: 16,
    lineHeight: 1.6,
    color: "#444",
    marginBottom: 24,
  },
  // Gate card
  gateCard: (isActive, isResolved) => ({
    background: isResolved ? "#fafaf8" : "#fff",
    borderLeft: `4px solid ${isResolved ? "#ccc" : "#2563EB"}`,
    borderRadius: 2,
    padding: "28px 32px",
    opacity: isResolved ? 0.6 : 1,
    transition: "opacity 0.3s ease, background 0.3s ease",
    pointerEvents: isResolved ? "none" : "auto",
  }),
  gateMeta: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#999",
    marginBottom: 16,
    textTransform: "uppercase",
  },
  gateQuestion: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.4,
    color: "#1a1a1a",
    marginBottom: 8,
  },
  gateSubtitle: {
    fontSize: 15,
    color: "#777",
    lineHeight: 1.5,
    marginBottom: 24,
  },
  // Option buttons (This/That with descriptions)
  optionBtn: (isChosen, isUnchosen) => ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "20px 24px",
    marginBottom: 12,
    border: isChosen
      ? "2px solid #2563EB"
      : isUnchosen
        ? "2px solid #e5e5e5"
        : "2px solid #ddd",
    borderRadius: 4,
    background: isChosen ? "#EFF4FF" : isUnchosen ? "#f9f9f7" : "#fff",
    cursor: isUnchosen ? "default" : "pointer",
    opacity: isUnchosen ? 0.4 : 1,
    transition: "all 0.2s ease",
  }),
  optionLabel: (isChosen) => ({
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.04em",
    color: isChosen ? "#2563EB" : "#1a1a1a",
    marginBottom: 6,
  }),
  optionDesc: {
    fontSize: 14,
    color: "#777",
    lineHeight: 1.5,
  },
  // Simple buttons (budget tier)
  simpleBtn: (isChosen, isUnchosen) => ({
    display: "inline-block",
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.04em",
    padding: "14px 32px",
    marginRight: 12,
    marginBottom: 8,
    borderRadius: 4,
    border: "2px solid transparent",
    cursor: isUnchosen ? "default" : "pointer",
    opacity: isUnchosen ? 0.4 : 1,
    background: isChosen ? "#2563EB" : "#fff",
    color: isChosen ? "#fff" : "#1a1a1a",
    borderColor: isChosen ? "#2563EB" : "#ddd",
    transition: "all 0.15s ease",
  }),
  // Connector
  connector: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    paddingLeft: 18,
    gap: 0,
  },
  connectorLine: {
    width: 3,
    background: "#2563EB",
    borderRadius: 1,
  },
  connectorLabel: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12,
    fontWeight: 700,
    color: "#2563EB",
    padding: "2px 0",
    marginLeft: 10,
  },
  // Offer card
  offerCard: {
    background: "#1a1a1a",
    borderRadius: 4,
    padding: "36px 32px",
    color: "#fff",
    animation: "fadeSlideIn 0.5s ease-out",
  },
  offerBadge: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#2563EB",
    textTransform: "uppercase",
    marginBottom: 16,
  },
  offerTitle: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 28,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 8,
  },
  offerPrice: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 18,
    fontWeight: 700,
    color: "#2563EB",
    marginBottom: 4,
  },
  offerPriceSub: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12,
    color: "#888",
    marginBottom: 24,
  },
  offerBody: {
    fontSize: 16,
    lineHeight: 1.7,
    color: "#ccc",
    marginBottom: 16,
  },
  offerDetail: {
    fontSize: 15,
    lineHeight: 1.7,
    color: "#999",
    marginBottom: 16,
  },
  offerBullet: {
    fontSize: 14,
    color: "#aaa",
    lineHeight: 1.8,
    paddingLeft: 16,
    marginBottom: 4,
  },
  offerCta: {
    display: "inline-block",
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 14,
    fontWeight: 700,
    padding: "16px 36px",
    background: "#2563EB",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    marginTop: 24,
    textDecoration: "none",
    letterSpacing: "0.02em",
  },
  // Multi-offer section
  multiOfferCard: {
    background: "#242424",
    borderRadius: 4,
    padding: "28px 24px",
    marginBottom: 16,
  },
  multiOfferTitle: {
    fontFamily: "'Outfit', 'Work Sans', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 4,
  },
  multiOfferPrice: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 15,
    fontWeight: 700,
    color: "#2563EB",
    marginBottom: 16,
  },
  // Reset button
  resetBtn: {
    fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: "#999",
    background: "none",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "10px 20px",
    cursor: "pointer",
    marginTop: 40,
    display: "block",
  },
};

// ============================================================
// COMPONENTS
// ============================================================

function Connector({ label }) {
  return (
    <div style={styles.connector}>
      <div style={{ ...styles.connectorLine, height: 24 }} />
      {label && <span style={styles.connectorLabel}>{label}</span>}
      <div style={{ ...styles.connectorLine, height: 24 }} />
    </div>
  );
}

function GateCard({ gate, isActive, isResolved, chosenAnswer, onAnswer }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isActive && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isActive]);

  const hasDescriptions = gate.optionA?.description;

  return (
    <div ref={ref} style={styles.gateCard(isActive, isResolved)}>
      <div style={styles.gateMeta}>
        {gate.label} | {gate.type === "this-that" ? "THIS / THAT" : "YES / NO"}
      </div>
      <div style={styles.gateQuestion}>{gate.question}</div>
      {gate.subtitle && (
        <div style={styles.gateSubtitle}>{gate.subtitle}</div>
      )}

      {hasDescriptions ? (
        <div>
          {[gate.optionA, gate.optionB].map((opt) => {
            const isChosen = chosenAnswer === opt.label;
            const isUnchosen = chosenAnswer && !isChosen;
            return (
              <button
                key={opt.label}
                style={styles.optionBtn(isChosen, isUnchosen)}
                onClick={() => !isResolved && onAnswer(opt.label)}
              >
                <div style={styles.optionLabel(isChosen)}>{opt.label}</div>
                {opt.description && (
                  <div style={styles.optionDesc}>{opt.description}</div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          {[gate.optionA, gate.optionB].map((opt) => {
            const isChosen = chosenAnswer === opt.label;
            const isUnchosen = chosenAnswer && !isChosen;
            return (
              <button
                key={opt.label}
                style={styles.simpleBtn(isChosen, isUnchosen)}
                onClick={() => !isResolved && onAnswer(opt.label)}
              >
                {isChosen ? "\u25C6 " : ""}
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SingleOffer({ offer }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  return (
    <div ref={ref} style={styles.offerCard}>
      <div style={styles.offerBadge}>{offer.badge}</div>
      <div style={styles.offerTitle}>{offer.title}</div>
      {offer.price && <div style={styles.offerPrice}>{offer.price}</div>}
      {offer.priceSub && (
        <div style={styles.offerPriceSub}>{offer.priceSub}</div>
      )}
      <div style={styles.offerBody}>{offer.body}</div>
      {offer.detail && <div style={styles.offerDetail}>{offer.detail}</div>}
      {offer.detail2 && <div style={styles.offerDetail}>{offer.detail2}</div>}
      {offer.bullets && (
        <div style={{ marginBottom: 8 }}>
          {offer.bullets.map((b, i) => (
            <div key={i} style={styles.offerBullet}>
              {"\u25C6"} {b}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 24 }}>
        <a href={offer.ctaLink || "#"} target="_blank" rel="noopener noreferrer" style={styles.offerCta}>
          {offer.cta}
        </a>
        {offer.learnMoreLink && (
          <a
            href={offer.learnMoreLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              color: "#888",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Learn more about this experience
          </a>
        )}
        {offer.chatLink && (
          <a
            href={offer.chatLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
              fontSize: 12,
              fontWeight: 600,
              color: "#666",
              textDecoration: "none",
            }}
          >
            Not sure yet? Let's chat first {"\u2192"}
          </a>
        )}
      </div>
    </div>
  );
}

function MultiOffer({ offer }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  return (
    <div ref={ref} style={styles.offerCard}>
      <div style={styles.offerBadge}>{offer.badge}</div>
      <div style={{ ...styles.offerTitle, marginBottom: offer.anayaIntro ? 16 : 24 }}>
        {offer.title}
      </div>

      {offer.anayaIntro && (
        <div style={{ ...styles.offerDetail, marginBottom: 24 }}>
          {offer.anayaIntro}
        </div>
      )}

      {offer.options.map((opt, i) => (
        <div key={i} style={styles.multiOfferCard}>
          <div style={styles.multiOfferTitle}>{opt.title}</div>
          <div style={styles.multiOfferPrice}>{opt.price}</div>
          <div style={{ ...styles.offerBody, fontSize: 14 }}>{opt.body}</div>
          {opt.detail && (
            <div style={{ ...styles.offerDetail, fontSize: 13 }}>
              {opt.detail}
            </div>
          )}
          {opt.bullets && (
            <div style={{ marginBottom: 8 }}>
              {opt.bullets.map((b, j) => (
                <div key={j} style={{ ...styles.offerBullet, fontSize: 13 }}>
                  {"\u25C6"} {b}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
            <a href={opt.ctaLink || "#"} target="_blank" rel="noopener noreferrer" style={{ ...styles.offerCta, fontSize: 12, padding: "12px 28px" }}>
              {opt.cta}
            </a>
            {opt.chatLink && (
              <a
                href={opt.chatLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#666",
                  textDecoration: "none",
                }}
              >
                Not sure yet? Let's chat first {"\u2192"}
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function NathanielQualifier() {
  const [started, setStarted] = useState(false);
  const [currentGateIndex, setCurrentGateIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const sequence = TREE_DEFINITION.resolveSequence(answers);

  const handleAnswer = (gateId, answer) => {
    const gateIndex = sequence.indexOf(gateId);
    if (gateIndex !== currentGateIndex) return;

    const newAnswers = { ...answers, [gateId]: answer };
    setAnswers(newAnswers);
    setCurrentGateIndex((prev) => prev + 1);
  };

  const reset = () => {
    setStarted(false);
    setCurrentGateIndex(0);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Check if the current position in the sequence is an offer
  // This happens when all gates have been answered and the next item is an offer
  const currentItemId = started ? sequence[currentGateIndex] : null;
  const isAtOffer = currentItemId?.startsWith("offer:") || false;
  
  // Also check if we've gone past the end (edge case)
  const isPastEnd = started && currentGateIndex >= sequence.length;

  const finalOfferId = isAtOffer
    ? currentItemId
    : isPastEnd && sequence[sequence.length - 1]?.startsWith("offer:")
      ? sequence[sequence.length - 1]
      : null;

  const offerKey = finalOfferId?.startsWith("offer:")
    ? finalOfferId.replace("offer:", "")
    : null;

  const finalOffer = offerKey ? TREE_DEFINITION.offers[offerKey] : null;

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&family=Work+Sans:wght@400;500;600&display=swap');
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { font-family: inherit; }
        @media (max-width: 768px) {
          .nq-container { padding: 24px 16px 60px !important; }
        }
      `}</style>

      <div className="nq-container" style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerMeta}>
            {"\u25C6"} Binary Game / Next Steps Qualifier
          </div>
          <div style={styles.headerTitle}>
            What should we do next, Nathaniel?
          </div>
          <div style={styles.headerSubtitle}>
            Binary Game Decision Tree // Prepared by Marco
          </div>
        </div>

        <div style={styles.divider} />

        {/* Intro card */}
        {!started ? (
          <div style={{ animation: "fadeSlideIn 0.5s ease-out" }}>
            <div style={styles.introCard}>
              <div style={styles.introHeading}>
                {TREE_DEFINITION.intro.heading}
              </div>
              <div style={styles.introBody}>{TREE_DEFINITION.intro.body}</div>
              <button
                style={{
                  ...styles.simpleBtn(false, false),
                  background: "#2563EB",
                  color: "#fff",
                  borderColor: "#2563EB",
                }}
                onClick={() => setStarted(true)}
              >
                {"\u25C6"} {TREE_DEFINITION.intro.cta}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Render gates */}
            {sequence.map((itemId, index) => {
              if (itemId.startsWith("offer:")) return null;

              const gate = TREE_DEFINITION.gates[itemId];
              if (!gate) return null;
              if (index > currentGateIndex) return null;

              const isActive = index === currentGateIndex;
              const isResolved = index < currentGateIndex;

              return (
                <div
                  key={gate.id}
                  style={{ animation: isActive ? "fadeSlideIn 0.4s ease-out" : "none" }}
                >
                  {index > 0 && (
                    <Connector
                      label={isResolved || isActive ? answers[sequence[index - 1]] : null}
                    />
                  )}
                  <GateCard
                    gate={gate}
                    isActive={isActive}
                    isResolved={isResolved}
                    chosenAnswer={answers[gate.id]}
                    onAnswer={(answer) => handleAnswer(gate.id, answer)}
                  />
                </div>
              );
            })}

            {/* Render offer */}
            {finalOffer && (
              <div>
                <Connector
                  label={answers[sequence[sequence.length - 2]]}
                />
                {finalOffer.options ? (
                  <MultiOffer offer={finalOffer} />
                ) : (
                  <SingleOffer offer={finalOffer} />
                )}
              </div>
            )}

            {/* Reset */}
            {finalOffer && (
              <button style={styles.resetBtn} onClick={reset}>
                RUN AGAIN
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
